import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoMeta from '@/components/feature/SeoMeta';
import { siteInfo } from '@/mocks/videos';

export default function About() {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '51黑料',
        url: 'https://www.51heiliao.com',
        description: siteInfo.description,
        foundingDate: siteInfo.founded,
        email: siteInfo.email,
        contactPoint: {
          '@type': 'ContactPoint',
          email: siteInfo.email,
          contactType: 'customer service',
          availableLanguage: ['Chinese'],
        },
      },
      {
        '@type': 'AboutPage',
        name: '关于51黑料 - 品牌介绍与口碑',
        description: '了解51黑料品牌故事、发展历程与平台优势，汇聚海量黑料视频和吃瓜视频的领先平台。',
        url: 'https://www.51heiliao.com/about',
        isPartOf: { '@type': 'WebSite', name: '51黑料' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.51heiliao.com/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.51heiliao.com/about' },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '51黑料平台的主要特点是什么？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '51黑料平台汇聚海量视频资源，涵盖吃瓜视频、黑料视频等多个分类。平台采用先进流媒体技术，确保高清流畅播放体验，每日更新最新视频内容。',
            },
          },
          {
            '@type': 'Question',
            name: '51黑料如何保证内容质量？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '51黑料拥有专业的内容审核团队，确保所有视频内容均符合平台规范。我们致力于为用户提供优质、真实的视频观看体验。',
            },
          },
        ],
      },
    ],
  };

  // Contact form submit URL
  const contactFormUrl = 'https://readdy.ai/api/form/d9opr00senbniolesjj0';

  const handleContactSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!contactFormUrl) return;

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim()) {
      // Bot detected, silently succeed
      return;
    }

    try {
      const urlParams = new URLSearchParams();
      for (const [key, value] of formData.entries()) {
        if (key === 'website_alt') continue;
        urlParams.append(key, value as string);
      }

      const response = await fetch(contactFormUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlParams.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        return;
      }

      if (response.ok && parsed?.code === 'OK') {
        form.reset();
        const successEl = document.getElementById('contact-success');
        if (successEl) successEl.classList.remove('hidden');
        setTimeout(() => successEl?.classList.add('hidden'), 5000);
      } else {
        const errorEl = document.getElementById('contact-error');
        const msgEl = document.getElementById('contact-error-msg');
        if (errorEl) errorEl.classList.remove('hidden');
        if (msgEl) {
          msgEl.textContent = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        }
        setTimeout(() => errorEl?.classList.add('hidden'), 5000);
      }
    } catch {
      const errorEl = document.getElementById('contact-error');
      if (errorEl) errorEl.classList.remove('hidden');
      setTimeout(() => errorEl?.classList.add('hidden'), 5000);
    }
  }, [contactFormUrl]);

  return (
    <>
      <SeoMeta
        title="关于51黑料 - 品牌介绍 | 51黑料网在线观看"
        description="了解51黑料品牌故事，汇聚海量黑料视频和吃瓜视频的领先平台。查看51黑料网在线观看平台的口碑评价与联系方式。"
        keywords="51黑料,51黑料网,关于51黑料,51黑料在线观看,51黑料视频平台,品牌介绍"
        canonical="https://www.51heiliao.com/about"
        schema={aboutSchema}
      />

      <Navbar />

      {/* Hero */}
      <section className="relative w-full min-h-[300px] md:min-h-[380px] flex items-center mt-16">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20office%20workspace%20with%20warm%20red%20and%20amber%20lighting%2C%20professional%20creative%20team%20environment%2C%20sleek%20interior%20design%2C%20collaborative%20atmosphere%2C%20cinematic%20lighting%2C%20high-end%20corporate%20aesthetic&width=1600&height=700&seq=about-hero-01&orientation=landscape"
          alt="关于51黑料"
          className="absolute inset-0 w-full h-full object-cover object-top"
          title="关于51黑料"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/55"></div>
        <div className="relative z-10 w-full px-4 md:px-6 max-w-7xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-white font-heading">
            关于<strong>51黑料</strong>
          </h1>
          <p className="text-white/75 mt-3 max-w-lg mx-auto text-sm md:text-base">
            了解我们的品牌故事与发展历程
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <nav className="w-full max-w-7xl mx-auto px-4 md:px-6 py-3" aria-label="面包屑导航">
        <ol className="flex items-center text-xs text-foreground-500 gap-1.5">
          <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link></li>
          <li><i className="ri-arrow-right-s-line"></i></li>
          <li className="text-foreground-700 font-medium">关于我们</li>
        </ol>
      </nav>

      <main className="w-full max-w-5xl mx-auto px-4 md:px-6 pb-16">
        {/* Brand Introduction */}
        <section className="mt-10">
          <h2 className="text-2xl font-bold text-foreground-900 font-heading mb-6">品牌介绍</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                51黑料成立于{siteInfo.founded}年，是国内领先的黑料视频和吃瓜视频聚合平台。我们致力于为用户提供最新、最全的娱乐八卦内容，打造一个充满活力的视频分享社区。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                作为51黑料网在线观看的核心平台，我们整合了吃瓜视频、黑料视频、直播、影视等多元化内容。平台采用先进的流媒体技术，确保用户获得流畅不卡顿的51黑料在线观看体验。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                我们拥有超过{siteInfo.stats.videos.toLocaleString()}条视频资源，服务超过{siteInfo.stats.users.toLocaleString()}万注册用户，日均播放量突破{siteInfo.stats.dailyViews.toLocaleString()}万次。
              </p>
            </div>
            <div className="rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20Chinese%20media%20company%20interior%2C%20warm%20modern%20office%20space%2C%20video%20production%20equipment%2C%20creative%20work%20environment%2C%20team%20collaboration%2C%20soft%20amber%20lighting%2C%20corporate%20headquarters&width=640&height=420&seq=about-brand-02&orientation=landscape"
                alt="51黑料品牌"
                className="w-full h-full object-cover"
                title="51黑料品牌"
              />
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="mt-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: siteInfo.stats.videos.toLocaleString() + '+', label: '视频资源' },
              { value: siteInfo.stats.users.toLocaleString() + '+', label: '注册用户' },
              { value: siteInfo.stats.dailyViews.toLocaleString() + '+', label: '日均播放' },
              { value: '24h', label: '内容更新' },
            ].map((stat) => (
              <div
                key={stat.label}
                className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70"
              >
                <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">
                  {stat.value}
                </div>
                <p className="text-xs text-foreground-600 mt-1.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Reputation */}
        <section className="mt-14">
          <h2 className="text-2xl font-bold text-foreground-900 font-heading mb-6">用户口碑</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                name: '吃瓜达人小王',
                rating: 5,
                text: '51黑料真的是我最喜欢的吃瓜平台，视频更新速度快，画质也很清晰。每天打开51黑料在线观看已经成为习惯！',
                date: '2026-07-28',
              },
              {
                name: '娱乐圈观察者',
                rating: 5,
                text: '用过很多类似平台，还是51黑料最好用。分类清晰，51黑料视频资源丰富，一区二区的整理也很完善。强烈推荐！',
                date: '2026-07-25',
              },
              {
                name: '追瓜小能手',
                rating: 4,
                text: '51黑料网在线观看体验很棒，视频加载很快不卡顿。吃瓜视频和黑料视频的内容质量都很高，每天都有惊喜。',
                date: '2026-07-20',
              },
            ].map((review) => (
              <div
                key={review.name}
                className="p-5 rounded-lg bg-background-50 border border-background-200/70"
              >
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i
                      key={i}
                      className={`text-sm ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                    ></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                  &ldquo;{review.text}&rdquo;
                </p>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-foreground-900">{review.name}</span>
                  <span className="text-foreground-500">{review.date}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Form */}
        <section className="mt-14" id="contact">
          <h2 className="text-2xl font-bold text-foreground-900 font-heading mb-6">联系我们</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们。我们期待与您沟通交流，共同打造更好的51黑料在线观看平台。
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">电子邮箱</p>
                    <p className="text-sm font-medium text-foreground-900">{siteInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 flex-shrink-0">
                    <i className="ri-time-line"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">工作时间</p>
                    <p className="text-sm font-medium text-foreground-900">周一至周日 9:00 - 22:00</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="bg-background-50 rounded-lg border border-background-200/70 p-6">
              <form
                onSubmit={handleContactSubmit}
                className="space-y-4"
                data-readdy-form
              >
                {/* Honeypot */}
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div>
                  <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    姓名
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    邮箱
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    required
                    className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-colors"
                    placeholder="请输入主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    留言
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-colors resize-none"
                    placeholder="请输入您的留言（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                </div>

                {/* Success Message */}
                <div id="contact-success" className="hidden p-3 rounded-lg bg-green-50 text-green-700 text-sm">
                  <i className="ri-check-line mr-1"></i>
                  提交成功！我们会尽快与您联系。
                </div>

                {/* Error Message */}
                <div id="contact-error" className="hidden p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                  <i className="ri-error-warning-line mr-1"></i>
                  <span id="contact-error-msg">提交失败，请稍后重试</span>
                </div>

                <button
                  type="submit"
                  className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  提交留言
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-14">
          <h2 className="text-2xl font-bold text-foreground-900 font-heading mb-6">常见问题</h2>
          <div className="space-y-3">
            {[
              { q: '51黑料平台的主要特点是什么？', a: '51黑料平台汇聚海量视频资源，涵盖吃瓜视频、黑料视频等多个分类。平台采用先进流媒体技术，确保高清流畅播放体验，每日更新最新视频内容。' },
              { q: '51黑料如何保证内容质量？', a: '51黑料拥有专业的内容审核团队，确保所有51黑料视频和吃瓜视频内容均符合平台规范。我们致力于为用户提供优质、真实的视频观看体验。' },
              { q: '51黑料网在线观看需要注册吗？', a: '51黑料在线观看大部分内容无需注册即可免费观看。注册用户可以享受更多个性化功能。' },
            ].map((faq, idx) => (
              <details key={idx} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors list-none">
                  <span><strong>{faq.q}</strong></span>
                  <i className="ri-arrow-down-s-line text-lg transition-transform duration-200 group-open:rotate-180"></i>
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-700 leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-14 text-center p-10 rounded-lg bg-gradient-to-r from-primary-500/10 to-accent-500/10 border border-primary-200/30">
          <h2 className="text-xl font-bold text-foreground-900 font-heading mb-3">
            加入51黑料，畅享精彩视频
          </h2>
          <p className="text-sm text-foreground-700 mb-6 max-w-md mx-auto">
            海量51黑料视频和吃瓜视频等你来看，立即开始你的51黑料在线观看之旅！
          </p>
          <Link
            to="/"
            className="inline-block px-8 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            立即观看 <i className="ri-arrow-right-line ml-1"></i>
          </Link>
        </section>
      </main>

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 shadow-lg hover:bg-primary-600 transition-all duration-200 z-40 cursor-pointer"
          aria-label="返回顶部"
        >
          <i className="ri-arrow-up-line"></i>
        </button>
      )}

      <Footer />
    </>
  );
}